<template>
  <main class="wrapper-content">
    <template v-if="!isLoading">
      <LmFormHeader :title="$t('Vehicle')" :name="form.name" />
      <a-tabs :default-active-key="isOnEditMode? '2' : '1'">
        <a-tab-pane key="1" :tab="$t('Edit')">
          <b-card class="border">
            <b-row>
              <LmTextInput
                ref="nameInput"
                class="col-12 col-md-6"
                :label="$t('Name')"
                v-model="$v.form.name.$model"
                :v="v('form.name')"
              />

              <LmTextArea
                class="col-12 col-md-6"
                :label="$t('Description')"
                v-model="form.description"
              />

              <LmTextInput
                class="col-12 col-md-6"
                :label="$t('Brand')"
                v-model="form.brand"
              />

              <LmTextInput
                class="col-12 col-md-6"
                :label="$t('Model')"
                v-model="form.model"
              />

              <LmNumberInputV2
                class="col-12 col-md-6"
                :label="$t('Phone')"
                v-model="form.phone"
              />

              <LmTreeSelect
                class="col-12 col-md-6"
                :label="$t('Provider')"
                :options="providerOptions"
                v-model="form.provider"
              />

              <LmNumberInputV2
                class="col-12 col-md-6"
                :label="$t('Capacity')"
                v-model="form.capacity"
              />
            </b-row>
          </b-card>
        </a-tab-pane>
        <a-tab-pane v-if="isOnEditMode" key="2" :tab="$t('Daily view')">
          <b-card>
            <LmDailyPrograms
              serviceEndpoint="programs"
              calendarProgramRoute="/programs/todos"
              newProgramRoute="/programs/new"
              passengerEditRoute="/passengers/edit/"
              editProgramRoute="/programs/edit/"
              servicesEditRoute="/services/edit/"
              hotelsEditRoute="/hotels/edit/"
              leadersEditRoute="/leaders/edit/"
              vehiclesEditRoute="/vehicles/edit/"
              :vehicleIdToFilter="$route.params.id"
            />
          </b-card>
        </a-tab-pane>
        <a-tab-pane  key="3" :tab="$t('Reportes')">
          <b-card>
            <label class="mb-3 block">Reporte por rango de fechas
              <a-tooltip placement="topLeft" :title="$t('Seleccione un rango de fechas para generar el reporte')">
                <font-awesome-icon class="clickable ml-1" icon="question-circle" />
              </a-tooltip>
            </label>
            <a-range-picker
              :ranges="{ Hoy: [moment(), moment()], 'Este mes': [moment().startOf('month'), moment().endOf('month')], 'Últimos 3 meses': [moment().subtract(2, 'months').startOf('month'), moment().endOf('month')] }"
              v-model="range"
              format="DD/MM/YYYY"
              :placeholder="['Desde', 'Hasta']"
            />

            <a-button type="primary" @click="downloadReport()" ghost :disabled="!hasRangeSelected" class="ml-6">
              <font-awesome-icon class="mr-1" icon="file-excel"></font-awesome-icon> {{$t('Generar reporte')}}
            </a-button>
          </b-card>
        </a-tab-pane>
      </a-tabs>

      <div :class="isOnModal ? 'flex items-center gap-1' : 'buttons'">
        <LmButton
          v-if="isOnModal"
          buttonClass="btn-secondary"
          :value="$t('close')"
          @click="onClose"
          :isProcessing="isProcessing"
        />
        <LmButton
          v-else
          buttonClass="btn-secondary"
          :value="$t('go_back')"
          @click="onGoBack"
          :isProcessing="isProcessing"
        />

        <LmButton
          buttonClass="btn-primary"
          :value="$t('save')"
          @click="onSubmit(false)"
          :isProcessing="isProcessing"
        />
        <LmButton
          v-if="isOnEditMode && !form.deleted"
          buttonClass="btn-danger"
          :value="$t('delete')"
          @click="onDeleteConfirmation"
          :isProcessing="isProcessing"
        />
        <LmButton
          v-if="isOnEditMode && form.deleted"
          value="Reactivar"
          buttonClass="btn-success"
          @click="onReactivateConfirmation"
          :isProcessing="isProcessing"
        />

      </div>
    </template>
    <b-row v-else>
      <b-col><b-skeleton type="input"></b-skeleton></b-col>
      <b-col><b-skeleton type="input"></b-skeleton></b-col>
      <b-col md="12" class="my-4">
        <div>
          <div class="d-flex flex-wrap mb-3" style="gap: 1rem">
            <span
              class="p-2 border rounded-lg d-flex align-items-center justify-content-between"
              style="gap: 1rem"
              v-for="(item, index) in 4"
              :key="index"
            >
              <b-skeleton type="avatar"></b-skeleton>
              <b-skeleton width="100px"></b-skeleton>
            </span>
          </div>
          <b-skeleton type="button" width="150px"></b-skeleton>
        </div>
      </b-col>
    </b-row>
  </main>
</template>

<script>
// Components
import LmButton from '@/components/form/elements/LmButton'
import LmDatePicker from '@/components/form/elements/LmDatePicker'
import LmTreeSelect from '@/components/form/elements/LmTreeSelect'
import LmTextInput from '../../components/form/elements/LmTextInput.vue'
import LmEditor from '@/components/form/elements/lmEditor'
import LmTextArea from '../../components/form/elements/LmTextArea.vue'
import LmNumberInputV2 from '@/components/form/elements/LmNumberInputV2.vue'
import LmDailyPrograms from '@/views/lumbretravel/LmDailyPrograms'

// Mixins
import lmStandardFormHelperMixin from '@/mixins/lmStandardFormHelperMixin'

// Utils
import { required } from 'vuelidate/lib/validators'
import commonsApi from '@/api/commons'
import moment from 'moment'

export default {
  mixins: [lmStandardFormHelperMixin],
  components: {
    LmDatePicker,
    LmTextInput,
    LmTextArea,
    LmEditor,
    LmTreeSelect,
    LmButton,
    LmDailyPrograms,
    LmNumberInputV2
  },
  props: {
    isOnEditMode: {
      type: Boolean,
      default: false
    },
    isOnModal: {
      type: Boolean,
      default: false
    },
    providersEndpoint: {
      type: String,
      required: true
    },
    serviceEndpoint: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      dateFormat: 'YYYY/MM/DD',
      monthFormat: 'YYYY/MM',
      range: [],
      isLoading: false,
      form: {
        name: '',
        description: '',
        brand: '',
        model: '',
        phone: '',
        capacity: '',
        provider: null,
        deleted: false
      },
      providerOptions: [],
      isProcessing: true
    }
  },
  validations: {
    form: {
      name: { required }
    }
  },
  computed: {
    hasRangeSelected () {
      return this.range.length > 0
    }
  },
  methods: {
    moment,
    async downloadReport () {
      const res = await commonsApi.axiosPostWrapper(`${process.env.VUE_APP_API_URL}programs/generate-searchby-spreadsheet`, {
        id: this.$route.params.id,
        range: this.range,
        searchBy: 'vehicle'
      })
      if (res.status === 200 && res.data.url) {
        const url = res.data.url
        const link = document.createElement('a')
        console.log('url:', url)
        link.href = url
        // link.download = 'data.csv'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    }
  },
  async created () {
    try {
      const providersResponse = await commonsApi.axiosGetWrapper(this.providersEndpoint)
      this.providerOptions = providersResponse.data.items.map(el => ({ id: el._id, name: el.name }))
    } catch (error) {
      console.error('Error fetching providers:', error)
    }
  }
}
</script>
